import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-cc-info.html';
import {ccInfoTranslations} from './translations.js';
import {TranslationsUtil} from '@nuskin/ns-util';
import {RunConfigService} from '@nuskin/ns-util';

/**
 * Helper class for ns-billing. Displays the name/number/expiration date of a credit card.
 * 
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>This component is meant to be used as a child of a parent that's able to pass down relevant data.
 *  It'll fetch a credit card type image from the content server.</p>
 * </section>
 *
 * <h2>CC Info Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-cc-info
 *        payment-type-id="1"
 *        card-type="MasterCard"
 *        last-four-digits="3321"
 *        expiration="12/23"></ns-cc-info>
 *   </template>
 * </demo-snippet>
 */
export class NsCcInfo extends PolymerElement {
  static get is () {
    return 'ns-cc-info';
  }
  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  constructor () {
    super();
    this.name = 'ns-cc-info';
  }

  static get properties () {
    return {
      /**
      * Country for translations
      */
      countryCode: {
        type: String
      },
      /**
      * Language for translations
      */
      language: {
        type: String
      },
      /**
       * Last four digits of the credit card number
       */
      lastFourDigits: {
        type: String
      },
      /**
       * Credit card expiration date
       */
      expiration: {
        type: String
      },
      /**
       * The name of the credit card holder
       */
      paymentName: {
        type: String
      },
      /**
       * True if the card has passed its expiration date, false if not.
       */
      isExpired: {
        type: Boolean,
        notify: true,
        value: false
      },
      /**
       * Whether the credit card will expire before the next order date.
       * If true, a "credit card expiring soon message" will be displayed.
       */
      isExpiringSoon: {
        type: Boolean,
        value: false
      },
      /**
       * Card type code (VISA, MC, JCB, CEDN, etc.)
       */
      cardType: {
        type: String,
        observer: '_setPaymentView'
      },
      /**
       * Localized card type label
       */
      cardTypeLabel: {
        type: String
      },
      /**
       * The number corresponding to the payment type. Visa = 0, MasterCard = 1, ...
       */
      paymentTypeId: {
        type: Number,
        observer: 'updateImage'
      },
      /**
       * Property to identify if the payment type is a credit card or not.
       */
      isCreditCard: {
        type: Boolean
      },
      /**
       * Property to identify if the payment type is a SEPA payment or not.
       */
      isSEPA: {
        type: Boolean
      },
      
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      hideCardholderName: {
        type: Boolean,
        value: false
      },
      hideLastFour: {
        type: Boolean,
        value: false
      },
      hideExpiration: {
        type: Boolean,
        value: false
      },
      /**
       * holder for the feature flags enabled for the user 
       */
      featureFlags: {
        type: Object,
        value: {}
      },
      isExpiringComputed: {
        type: Boolean,
        computed: 'isExpiredOrExpiring(isExpired, isExpiringSoon)'
      },
      endingInTextComputed: {
        type: String,
        computed: 'computeLastFourText(endingIn, lastFourDigits)'
      },
      expiresOnTextComputed: {
        type: String,
        computed: 'computeExpirationText(expirationDateText, expiration)'
      },
      expiringSoonTextComputed: {
        type: String,
        computed: 'computeExpirationText(expiringSoonText, expiration)'
      },
      expiredTextComputed: {
        type: String,
        computed: 'computeExpirationText(expiredText, expiration)'
      }
    };
  }

  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, ccInfoTranslations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });

  }

  /**
   * Observer to update the payment type icon based on the current payment type id
   */
  updateImage() {
    this.cardTypeImage = RunConfigService.getBaseUrl() + "/content/dam/apps/checkout/creditcards/CC-" + this.paymentTypeId + ".png";
  }

  /**
   * @param {string} message expiration message
   * @param {string} expDate expiration date text
   */
  computeExpirationText(message, expDate) {
    return (message || '').replace(/{expiration}/g, expDate);
  }

  /**
   * @param {string} text 
   * @param {string} lastFour 
   */
  computeLastFourText(text, lastFour) {
    return (text || '').replace(/{lastFour}/g, lastFour);
  }

  isExpiredOrExpiring(isExpired, isExpiring) {
    return isExpired || isExpiring;
  }

  /**
   * Observer for card type
   */
  _setPaymentView (e) {
    console.log('Set payment view: ', e);
    switch(e) {
      case 'CEDN':
      case 'Cedyna':
      case 'CR':
        this.hideCardholderName = true;
        this.hideExpiration = true;
        this.hideLastFour = false;
        this.isSEPA = false;
        break;
      case 'SDD':
      case 'SEPA Direct Debit':
        this.hideCardholderName = false;
        this.hideExpiration = false;
        this.hideLastFour = false;
        this.isSEPA = true;
        break;
      case 'WIRE':
        this.hideCardholderName = false;
        this.hideExpiration = true;
        this.hideLastFour = true;
        this.isSEPA = false;
        break;
      default:
        this.hideCardholderName = false;
        this.hideExpiration = false;
        this.hideLastFour = false;
        this.isSEPA = false;
        break;
    }
  }
}

customElements.define(NsCcInfo.is, NsCcInfo);
